/* eslint-disable */
import React, { useMemo, useState, useRef, useEffect } from 'react';
import RangePicker from './components/RangePicker';

import { 
    MONTH_NAMES
} from './constants';
import { 
    useCalendarState 
} from './hooks/useCalendarState';
import { useCalendarLayout } from './hooks/useCalendarLayout';
import { useCalendarInteractions } from './hooks/useCalendarInteractions';
import { CalendarHeader } from './components/CalendarHeader';
import { MonthGrid } from './components/MonthGrid';
import { TimelineGrid } from './components/TimelineGrid';



/**
 * Hauptkomponente für die Kalenderansicht (Leader-Tool).
 * Orchestriert den Status, das Layout und die Interaktionen über spezialisierte Hooks
 * und rendert die entsprechende UI (MonthGrid oder TimelineGrid).
 * 
 * @param {Object} props - Component Props
 * @param {Array<Object>} props.assignments - Liste der anzuzeigenden Einsätze.
 * @param {Array} props.workers - All workers for conflict checking
 * @param {Array} props.vehicles - All vehicles for conflict checking
 * @param {Array} props.kolonnen - All kolonnen for conflict checking
 * @param {boolean} props.isReadOnly - Whether interactions are restricted (Rank 2)
 * @param {number} props.viewDays - Anzahl der Tage in der Timeline-Ansicht.
 * @param {string} props.viewMode - Aktueller Modus ('month' | 'days').
 * @param {Date} props.startDate - Startdatum der aktuellen Ansicht.
 * @param {Function} props.onNavigate - Navigiert zu einem neuen Datum oder 'today'.
 * @param {Function} props.onViewModeChange - Wechselt zwischen Monat und Tagen.
 * @param {Function} props.onViewChange - Ändert die Anzahl der sichtbaren Tage.
 * @param {Function} props.onSetStartDate - Setzt ein spezifisches Startdatum.
 * @param {Function} props.onDayClick - Handler für Klicks auf freie Flächen.
 * @param {Function} props.onAssignmentClick - Handler für Klicks auf Einsätze.
 * @param {Function} props.onAssignmentUpdate - Callback für Änderungen an Einsätzen.
 * @param {Function} props.onDropWorker - Handler für Drag & Drop von Ressourcen.
 * @param {boolean} props.isSidebarOpen - Status der Seitenleiste für Layout-Berechnungen.
 * @param {Object} props.onContextMenu - Handler für das Rechtsklick-Menü.
 */
const CalendarView = ({
    assignments,
    viewDays,
    viewMode,
    startDate,
    onNavigate,
    onViewModeChange,
    onViewChange,
    onSetStartDate,
    onDayClick,
    onAssignmentClick,
    onAssignmentHover,
    onAssignmentHoverEnd,
    onConflictHover,
    onConflictHoverEnd,
    onAssignmentUpdate,
    onAssignmentDelete,
    onDropWorker,
    onNewAssignment,
    onUndo,
    onRedo,
    canUndo,
    canRedo,
    onSwitchToList,
    onExport,
    onMoveToUndated,
    isSidebarOpen,
    isConflictDetectionEnabled = true,
    externalDraggingAssignment,
    interaction,
    setInteraction,
    onContextMenu,
    clipboard,
    // New: Pass conflict data for tile indicators
    absences = [],
    assignmentsForConflicts = [],
    absencesForConflicts = [],
    workers = [],
    vehicles = [],
    kolonnen = [],
    isReadOnly = false
}) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const {
        scrollRef,
        containerRef,
        showRangePicker, setShowRangePicker,
        isAllDayCollapsed, setIsAllDayCollapsed,
        hourStart, setHourStart,
        hourEnd, setHourEnd,
        alldayWidth, setAlldayWidth,
        resizingAllday, setResizingAllday,
        hoveringAlldayResizer, setHoveringAlldayResizer,
        interactionEnded, setInteractionEnded,
        hasManuallyResized, setHasManuallyResized,
        dynamicRowHeight,
        containerWidth,
        scrollX,
        now,
        nowHour,
        actualAlldayWidth,
        GRID_BUFFER,
        dynamicHourWidth,
        nowLeft,
        TIMELINE_WIDTH
    } = useCalendarState({ viewDays, viewMode });

    const {
        days,
        dateLabel,
        showsToday,
        allDayByKey,
        timedByKey,
        maxAllDayCount,
        allDayLayouts,
        assignmentConflictsMap,
        monthDragPreview
    } = useCalendarLayout({
        viewMode,
        startDate,
        viewDays,
        assignments,
        isSidebarOpen,
        interaction,
        assignmentsForConflicts,
        absencesForConflicts,
        workers,
        vehicles,
        kolonnen,
        today
    });

    // Container und Sizing wurden in useCalendarState ausgelagert


    // Interaktions-Logik (Move, Resize, Drag & Drop)
    const {
        handleInteractionStart,
        getTimedLayout,
        handleDrop,
        handleDropAllDay,
        handleDragOver,
        handleDragLeave,
        handleMonthDragStart,
        handleMonthDragEnd
    } = useCalendarInteractions({
        interaction,
        setInteraction,
        interactionEnded,
        setInteractionEnded,
        assignments,
        onAssignmentUpdate,
        onDropWorker,
        scrollRef,
        actualAlldayWidth,
        dynamicHourWidth,
        hourStart,
        GRID_BUFFER,
        externalDraggingAssignment
    });

    const handleMonthNavigate = (dir) => {
        const d = new Date(startDate);
        d.setMonth(d.getMonth() + dir);
        d.setDate(1);
        onSetStartDate(d);
    };

    const navigateAmount = viewMode === 'month' ? 1 : viewDays;




    return (
        <div className="leader-main">
            {/* Navigation Header */}
            <CalendarHeader
                isSidebarOpen={isSidebarOpen}
                viewMode={viewMode}
                dateLabel={dateLabel}
                handleMonthNavigate={handleMonthNavigate}
                onNavigate={onNavigate}
                navigateAmount={navigateAmount}
                showsToday={showsToday}
                setShowRangePicker={setShowRangePicker}
                canUndo={canUndo}
                canRedo={canRedo}
                onUndo={onUndo}
                onRedo={onRedo}
                hourStart={hourStart}
                setHourStart={setHourStart}
                hourEnd={hourEnd}
                setHourEnd={setHourEnd}
                viewDays={viewDays}
                onViewChange={onViewChange}
                onViewModeChange={onViewModeChange}
                onNewAssignment={onNewAssignment}
                onSwitchToList={onSwitchToList}
                onExport={onExport}
                isReadOnly={isReadOnly}
            />

            {/* Month view */}
            {viewMode === 'month' ? (
                <MonthGrid
                    startDate={startDate}
                    assignments={assignments}
                    today={today}
                    onDayClick={onDayClick}
                    onAssignmentClick={onAssignmentClick}
                    onAssignmentHover={onAssignmentHover}
                    onAssignmentHoverEnd={onAssignmentHoverEnd}
                    onConflictHover={onConflictHover}
                    onConflictHoverEnd={onConflictHoverEnd}
                    assignmentConflictsMap={assignmentConflictsMap}
                    onContextMenu={onContextMenu}
                    handleDrop={handleDrop}
                    handleDragOver={handleDragOver}
                    handleDragLeave={handleDragLeave}
                    handleMonthDragStart={handleMonthDragStart}
                    handleMonthDragEnd={handleMonthDragEnd}
                    interaction={interaction}
                    monthDragPreview={monthDragPreview}
                    isConflictDetectionEnabled={isConflictDetectionEnabled}
                    isReadOnly={isReadOnly}
                />
            ) : (
                /* ===== HORIZONTAL TIMELINE VIEW ===== */
                <TimelineGrid
                    containerRef={containerRef}
                    scrollRef={scrollRef}
                    scrollX={scrollX}
                    days={days}
                    hourStart={hourStart}
                    hourEnd={hourEnd}
                    dynamicHourWidth={dynamicHourWidth}
                    actualAlldayWidth={actualAlldayWidth}
                    GRID_BUFFER={GRID_BUFFER}
                    TIMELINE_WIDTH={TIMELINE_WIDTH}
                    dynamicRowHeight={dynamicRowHeight}
                    isAllDayCollapsed={isAllDayCollapsed}
                    setIsAllDayCollapsed={setIsAllDayCollapsed}
                    hoveringAlldayResizer={hoveringAlldayResizer}
                    setHoveringAlldayResizer={setHoveringAlldayResizer}
                    resizingAllday={resizingAllday}
                    setResizingAllday={setResizingAllday}
                    setHasManuallyResized={setHasManuallyResized}
                    alldayWidth={alldayWidth}
                    setAlldayWidth={setAlldayWidth}
                    allDayLayouts={allDayLayouts}
                    timedByKey={timedByKey}
                    interaction={interaction}
                    setInteraction={setInteraction}
                    interactionEnded={interactionEnded}
                    handleInteractionStart={handleInteractionStart}
                    getTimedLayout={getTimedLayout}
                    handleDrop={handleDrop}
                    handleDropAllDay={handleDropAllDay}
                    handleDragOver={handleDragOver}
                    handleDragLeave={handleDragLeave}
                    onDayClick={onDayClick}
                    handleContextMenu={onContextMenu}
                    onAssignmentClick={onAssignmentClick}
                    onAssignmentHover={onAssignmentHover}
                    onAssignmentHoverEnd={onAssignmentHoverEnd}
                    onConflictHover={onConflictHover}
                    onConflictHoverEnd={onConflictHoverEnd}
                    assignmentConflictsMap={assignmentConflictsMap}
                    today={today}
                    nowHour={nowHour}
                    nowLeft={nowLeft}
                    isConflictDetectionEnabled={isConflictDetectionEnabled}
                    isReadOnly={isReadOnly}
                />
            )}

            {showRangePicker && (
                <RangePicker 
                    initialStart={startDate}
                    initialEnd={days[days.length - 1]}
                    onSelect={(start, end) => {
                        onSetStartDate(start);
                        const diffTime = Math.abs(end - start);
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                        onViewChange(diffDays);
                        setShowRangePicker(false);
                    }}
                    onClose={() => setShowRangePicker(false)}
                />
            )}

            {/* Universal Context Menu is now rendered in LeaderPage.jsx */}
        </div>
    );
};

export default CalendarView;
